import { renameKeysFromRESTResponseToSDKResponse } from '@wix/sdk-runtime/rename-all-nested-keys';
import { transformRESTDocumentToSDKDocument } from '@wix/sdk-runtime/transformations/document';
import { transformRESTImageToSDKImage } from '@wix/sdk-runtime/transformations/image';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { EventDefinition, HttpClient, NonNullablePaths } from '@wix/sdk-types';
import {
  AttachmentCreatedEnvelope,
  AttachmentDeletedEnvelope,
  ContactAttachment,
  DeleteAttachmentIdentifiers,
  GenerateAttachmentUploadUrlOptions,
  GenerateAttachmentUploadUrlResponse,
  GetAttachmentIdentifiers,
  ListAttachmentsOptions,
  ListAttachmentsResponse,
  deleteAttachment as universalDeleteAttachment,
  generateAttachmentUploadUrl as universalGenerateAttachmentUploadUrl,
  getAttachment as universalGetAttachment,
  listAttachments as universalListAttachments,
} from './contacts-v4-attachment-attachments.universal.js';

export const __metadata = { PACKAGE_NAME: '@wix/crm' };

export function generateAttachmentUploadUrl(
  httpClient: HttpClient
): GenerateAttachmentUploadUrlSignature {
  return (
    contactId: string,
    fileName: string,
    options: NonNullablePaths<GenerateAttachmentUploadUrlOptions, `mimeType`, 2>
  ) =>
    universalGenerateAttachmentUploadUrl(
      contactId,
      fileName,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface GenerateAttachmentUploadUrlSignature {
  /**
   * Generates an upload URL for uploading a file as an attachment to a specified contact.
   *
   * To learn how to use the generated upload URL in the response to upload an attachment file,
   * see the [Upload API](https://dev.wix.com/docs/rest/assets/media/media-manager/files/upload-api).
   * @param - ID of the contact for whom the attachment is being uploaded.
   * @param - File name of the attachment including the extension, for example, `contact-cv.pdf`.
   */
  (
    contactId: string,
    fileName: string,
    options: NonNullablePaths<GenerateAttachmentUploadUrlOptions, `mimeType`, 2>
  ): Promise<
    NonNullablePaths<GenerateAttachmentUploadUrlResponse, `uploadUrl`, 2>
  >;
}

export function listAttachments(
  httpClient: HttpClient
): ListAttachmentsSignature {
  return (contactId: string, options?: ListAttachmentsOptions) =>
    universalListAttachments(
      contactId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface ListAttachmentsSignature {
  /**
   * Retrieves a list of attachments associated with a specified contact.
   * @param - Contact ID for the attachments to be listed.
   */
  (contactId: string, options?: ListAttachmentsOptions): Promise<
    NonNullablePaths<
      ListAttachmentsResponse,
      | `attachments`
      | `attachments.${number}._id`
      | `attachments.${number}.fileName`
      | `attachments.${number}.mimeType`
      | `attachments.${number}.attachmentType`,
      4
    >
  >;
}

export function deleteAttachment(
  httpClient: HttpClient
): DeleteAttachmentSignature {
  return (
    identifiers: NonNullablePaths<
      DeleteAttachmentIdentifiers,
      `attachmentId` | `contactId`,
      2
    >
  ) =>
    universalDeleteAttachment(
      identifiers,
      // @ts-ignore
      { httpClient }
    );
}

interface DeleteAttachmentSignature {
  /**
   * Deletes the specified attachment from the specified contact.
   */
  (
    identifiers: NonNullablePaths<
      DeleteAttachmentIdentifiers,
      `attachmentId` | `contactId`,
      2
    >
  ): Promise<void>;
}

export function getAttachment(httpClient: HttpClient): GetAttachmentSignature {
  return (
    identifiers: NonNullablePaths<
      GetAttachmentIdentifiers,
      `attachmentId` | `contactId`,
      2
    >
  ) =>
    universalGetAttachment(
      identifiers,
      // @ts-ignore
      { httpClient }
    );
}

interface GetAttachmentSignature {
  /**
   * Retrieves the specified attachment for the specified contact.
   * @returns The requested attachment.
   */
  (
    identifiers: NonNullablePaths<
      GetAttachmentIdentifiers,
      `attachmentId` | `contactId`,
      2
    >
  ): Promise<
    NonNullablePaths<
      ContactAttachment,
      `_id` | `fileName` | `mimeType` | `attachmentType`,
      2
    >
  >;
}

export const onAttachmentCreated = EventDefinition(
  'wix.contacts.v4.attachment_created',
  true,
  (event: AttachmentCreatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [{ path: 'entity.image' }, { path: 'entity.previewImage' }],
        },
        {
          transformFn: transformRESTDocumentToSDKDocument,
          paths: [{ path: 'entity.document' }],
        },
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [{ path: 'metadata.eventTime' }],
        },
      ])
    )
)<AttachmentCreatedEnvelope>();
export const onAttachmentDeleted = EventDefinition(
  'wix.contacts.v4.attachment_deleted',
  true,
  (event: AttachmentDeletedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [
            { path: 'undefined.image' },
            { path: 'undefined.previewImage' },
          ],
        },
        {
          transformFn: transformRESTDocumentToSDKDocument,
          paths: [{ path: 'undefined.document' }],
        },
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [{ path: 'metadata.eventTime' }],
        },
      ])
    )
)<AttachmentDeletedEnvelope>();

export {
  AccountInfo,
  AccountInfoMetadata,
  ActionEvent,
  AttachmentCreatedEnvelope,
  AttachmentDeletedEnvelope,
  AttachmentMedia,
  AttachmentMediaMediaOneOf,
  AttachmentMetadata,
  AttachmentSource,
  AttachmentType,
  BaseEventMetadata,
  ContactAttachment,
  ContactAttachmentMediaOneOf,
  CreateDemoAttachmentRequest,
  CreateDemoAttachmentResponse,
  CrmAttachment,
  CrmAttachmentAttachmentType,
  CrmAttachmentUploadedEvent,
  DeleteAttachmentIdentifiers,
  DeleteAttachmentRequest,
  DeleteAttachmentResponse,
  DemoContactType,
  DomainEvent,
  DomainEventBodyOneOf,
  Empty,
  EntityCreatedEvent,
  EntityDeletedEvent,
  EntityUpdatedEvent,
  EventMetadata,
  GenerateAttachmentUploadUrlOptions,
  GenerateAttachmentUploadUrlRequest,
  GenerateAttachmentUploadUrlResponse,
  GetAttachmentIdentifiers,
  GetAttachmentRequest,
  GetAttachmentResponse,
  IdentificationData,
  IdentificationDataIdOneOf,
  ListAttachmentsOptions,
  ListAttachmentsRequest,
  ListAttachmentsResponse,
  MessageEnvelope,
  Paging,
  PagingMetadata,
  RestoreInfo,
  WebhookIdentityType,
} from './contacts-v4-attachment-attachments.universal.js';
